import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { languages } from '../i18n';
import { Button } from './ui/button';

const LanguageSwitcher = ({ variant = 'default' }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    setIsOpen(false);
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Position dropdown when opening
  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
        zIndex: 999999
      });
    }
    setIsOpen(!isOpen);
  };

  // Compact variant - single flag button with horizontal dropdown
  if (variant === 'compact' || variant === 'flags') {
    return (
      <div className="relative" ref={containerRef}>
        <Button 
          ref={buttonRef}
          variant="ghost" 
          size="sm" 
          className="px-2 hover:bg-slate-100 rounded-lg" 
          data-testid="language-switcher-btn"
          onClick={handleToggle}
        >
          <img 
            src={currentLanguage.flag} 
            alt={currentLanguage.code}
            className="w-6 h-4 rounded-sm object-cover"
            style={{ minWidth: '24px' }}
          />
        </Button>
        
        {isOpen && (
          <div 
            className="bg-white border border-slate-200 rounded-lg shadow-lg p-2"
            style={dropdownStyle}
          >
            <div className="flex items-center gap-1">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`p-1.5 rounded-md transition-all ${
                    i18n.language === lang.code 
                      ? 'ring-2 ring-indigo-500 bg-indigo-50' 
                      : 'hover:bg-slate-100'
                  }`}
                  data-testid={`lang-option-${lang.code}`}
                  title={lang.name}
                >
                  <img 
                    src={lang.flag} 
                    alt={lang.code}
                    className="w-6 h-4 rounded-sm object-cover"
                    style={{ minWidth: '24px' }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full design for profile page - vertical list with names
  return (
    <div className="relative" ref={containerRef}>
      <Button 
        ref={buttonRef}
        variant="outline" 
        className="gap-2" 
        data-testid="language-switcher-btn"
        onClick={handleToggle}
      >
        <img 
          src={currentLanguage.flag} 
          alt={currentLanguage.code}
          className="w-5 h-4 rounded-sm object-cover"
          style={{ minWidth: '20px' }}
        />
        <span>{currentLanguage.name}</span>
      </Button>
      
      {isOpen && (
        <div 
          className="bg-white border border-slate-200 rounded-lg shadow-lg p-2 min-w-[160px]"
          style={dropdownStyle}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md transition-all text-left ${
                i18n.language === lang.code 
                  ? 'bg-indigo-50 text-indigo-600' 
                  : 'hover:bg-slate-100'
              }`}
              data-testid={`lang-option-${lang.code}`}
            >
              <img 
                src={lang.flag} 
                alt={lang.code}
                className="w-5 h-4 rounded-sm object-cover"
                style={{ minWidth: '20px' }}
              />
              <span className="text-sm">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
